/**
 * useFilterUrlSync [T114-F6-b] + R50
 *
 * Syncs FilterState to/from URL query params via React Router v6 useSearchParams.
 * Priority on page load: URL params > localStorage > defaults.
 * Omits default values (period=30d) from URL.
 * Debounces URL writes by 300ms to avoid excessive history entries.
 *
 * R50: URL parsing is delegated to the canonical `filtersFromQueryString` from
 * `@/lib/filters`, so the additive '2d' period (alongside legacy 7d/30d, the
 * R47 preset list, and the `custom` marker) is supported without duplicating
 * the period allowlist or the FILTER_PARAM_KEYS table here. Single source of
 * truth lives in filters.ts — this hook is the bridge between React and that
 * canonical contract.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_FILTER_PERIOD,
  FILTER_PARAM_KEYS_LIST,
  filtersFromQueryString,
  type FilterState,
} from '@/lib/filters';

/** Serialize FilterState into URLSearchParams, omitting defaults.
 *  Exported so non-hook callers (e.g. drill-through composers in
 *  `TrendsV2Skin`) can compose deterministic URLs without depending
 *  on the React context this file otherwise owns.
 *
 *  Period allowlist is intentionally NOT duplicated here — see
 *  `@/lib/filters` for the canonical period contract (R47 + R50 '2d' +
 *  legacy 7d/30d + custom). Default omission still uses the canonical
 *  DEFAULT_FILTER_PERIOD so URL write-through and URL read-from agree. */
export function filtersToParams(filters: FilterState): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.period && filters.period !== DEFAULT_FILTER_PERIOD) {
    params.set('period', filters.period);
  }
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom.toISOString());
  if (filters.dateTo) params.set('dateTo', filters.dateTo.toISOString());
  if (filters.type) params.set('type', filters.type);
  if (filters.accountIds?.length) params.set('accounts', filters.accountIds.join(','));
  if (filters.categoryIds?.length) params.set('categories', filters.categoryIds.join(','));
  // [T270-PY] Day-of-week serialization
  if (filters.dayOfWeek?.length) params.set('dow', filters.dayOfWeek.join(','));
  if (filters.amountMin != null) params.set('amountMin', String(filters.amountMin));
  if (filters.amountMax != null) params.set('amountMax', String(filters.amountMax));
  if (filters.tags?.length) params.set('tags', filters.tags.join(','));
  if (filters.isRecurring) params.set('recurring', 'true');
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('q', filters.search);

  return params;
}

export function useFilterUrlSync(): {
  filtersFromUrl: FilterState | null;
  syncFiltersToUrl: (filters: FilterState) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track latest searchParams in a ref to avoid adding it to useCallback deps
  // (which would cause syncFiltersToUrl to recreate on every URL change → infinite loop
  //  via Transactions.tsx useEffect([filters, syncFiltersToUrl]))
  const searchParamsRef = useRef(searchParams);
  useEffect(() => { searchParamsRef.current = searchParams; }, [searchParams]);

  // Parse URL on mount only (captured once). Parsing delegates to the
  // canonical `filtersFromQueryString` from @/lib/filters, which owns the
  // additive period allowlist (R47 + R50 '2d' + legacy 7d/30d + custom)
  // and the shared date / type / status / CSV parsing.
  const filtersFromUrl = useMemo(
    () => filtersFromQueryString(searchParams.toString()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // intentionally empty — read URL only on mount
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const syncFiltersToUrl = useCallback(
    (filters: FilterState) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const nextParams = filtersToParams(filters);
        // Preserve non-filter params (e.g. ?subscriptionId=) using the ref — stable, no loop risk.
        // FILTER_PARAM_KEYS_LIST is the canonical exported tuple from @/lib/filters; the cast
        // to `readonly string[]` keeps TypeScript happy with the heterogeneous `key: string`
        // without weakening the allowlist source.
        searchParamsRef.current.forEach((value, key) => {
          if (!(FILTER_PARAM_KEYS_LIST as readonly string[]).includes(key)) {
            nextParams.set(key, value);
          }
        });
        setSearchParams(nextParams, { replace: true });
      }, 300);
    },
    [setSearchParams], // searchParamsRef is a stable ref — no re-render loop
  );

  return { filtersFromUrl, syncFiltersToUrl };
}